import { PanelHeaderProps } from '../utilities/PanelHeader/types';

export interface Rect {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export interface PopoverTheme {
  backgroundColor?: string;
  boxShadow?: string;
  colorLabel?: string;
  fontFamily?: string;
  fontWeight?: number;
  fontSize?: string;
  lineHeight?: string;
  colorIcon?: string;
  colorIconActive?: string;
  borderRadius?: string;
  padding?: string;
  margin?: string;
  contentOverflowY?: string;
  textTransform?: string;
  headPadding?: string;
  zIndexTip?: number;
  zIndex?: number;
  hasHeadBorder?: boolean;
  cursor?: string;
  borderBottomLeftRadius?: string;
  iconHeight?: string;
}

export enum PopoverAlign {
  Start = 'start',
  Center = 'center',
  End = 'end',
}

export enum PopoverPlacement {
  Bottom = 'bottom',
  Top = 'top',
  Right = 'right',
  Left = 'left',
}

export interface PopoverProps {
  /**
   * Label of the popover
   */
  label?: React.ReactNode;
  /**
   * Width of the popover
   */
  width?: string;
  /**
   * On which side of the target should the popover be placed
   */
  placement?: PopoverPlacement;
  /**
   * How should the popover be aligned in relation to the target
   */
  align?: PopoverAlign;
  /**
   * The content that is shown within the popover
   */
  content: React.ReactNode;
  /**
   * A function that is triggered when the popover is closed
   */
  onClose?: () => void;
  /**
   * A function that is triggered before the popover might be closed
   */
  onWillClose?: () => void;
  /**
   * ignore events outside the popover that would close it
   */
  ignoreEventOutside?: boolean;
  /**
   * A function that is triggered when the popover is opened
   */
  onOpened?: () => void;
  /**
   * A function that is triggered when the mouse leaves the popover
   */
  onMouseLeave?: () => void;
  /**
   * Padding (in px) between popover and target
   */
  padding?: number;
  /**
   * margin around the popover
   */
  margin?: string;
  /**
   * margin around the popover tip
   */
  marginForTip?: string;
  /**
   * Don't render the tip
   */
  hideTip?: boolean;
  /**
   * Popover styles theme
   */
  theme?: PopoverTheme;
  /**
   * Whether to show header or not
   */
  showHeader?: boolean;
  /**
   * a ref to the child object that is triggering the popover
   */
  targetRef?: (ref: React.MutableRefObject<HTMLElement | Rect | null>) => void;
  /**
   * a ref that contains the toggle function for special-case use
   */
  toggleRef?:
    | ((ref: { toggle: () => void }) => void)
    | React.MutableRefObject<{ toggle: () => void } | null | undefined>;
  /**
   * A ref to the wrapper element of the popover.
   * Useful to have popovers inside other popovers.
   */
  wrapperRef?: React.MutableRefObject<HTMLDivElement | null>;
  /**
   * A ref to calculate the position of the popover.
   */
  positionRef?: React.MutableRefObject<HTMLElement | null>;
  /**
   * an id that can be used for testing
   * added as data-testid to the content of the popover
   */
  dataTestId?: string;
  /**
   * Whether the popover is open. This way it can be controlled from
   * outside.
   */
  open?: boolean;
  /**
   * Whether to open the popover when hovering over it
   */
  useHover?: boolean;
  /**
   * onClick handler
   */
  onClick?: (event: React.PointerEvent<Element>) => void;
  /**
   * onTouchStart handler
   */
  onTouchStart?: () => void;
  /**
   * onToggle handler (from closed -> open and open -> closed)
   */
  onToggle?: () => void;
  /**
   * Container? TODO explain
   */
  container?: React.MutableRefObject<HTMLElement | null>;
  /**
   * Handler for when clicking outside the popover's
   * content
   */
  onClickOutside?: () => void;
  /**
   * className attribute
   */
  className?: string;
  /**
   * a margin from the wrapper boundaries
   */
  wrapperMargin?: number;
  /**
   * Custom element for header
   */
  customHeaderElement?: React.ReactNode;
  /**
   * Whether the popover should update its position when the content size changes
   */
  updatePositionOnContentSizeChange?: boolean;
  /**
   * A key that can be used to force the popover to update its position
   */
  forceUpdatePositionKey?: string | number;
  /**
   * Whether to hide the close icon
   */
  hideCloseIcon?: boolean;
  /**
   * Whether to show the pointer line
   */
  showPointerLine?: boolean;
  closeIconType?: PanelHeaderProps['closeIconType'];
  openDelay?: number;
  closeDelay?: number;
  /**
   * Override the calculated position of the arrow tip
   */
  arrowTipPositionOverride?: number;
}
